// eslint-disable-next-line no-restricted-imports
import {useChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {getSelectedThread} from '@github-ui/copilot-chat-state/get-selected-thread'
import {sendEvent} from '@github-ui/hydro-analytics'
import {UnlinkIcon} from '@primer/octicons-react'
import {useCallback, useMemo} from 'react'

import type {TimelineEventTextReference} from '../../../timeline-events/timeline-events-types'
import {
  type DraftOrExistingIssue,
  makeReferenceFromExistingIssue,
  makeReferenceFromVersionedItem,
} from '../../content-preview-types'
import {useContentPreview} from '../../ContentPreviewContext'
import {type TreeNode, visit} from '../use-draft-issue-tree-map'
import {RelationshipList, type RelationshipListItem} from './RelationshipList'

function useUnlinkSubIssueCallback() {
  const manager = useChatManager()
  const state = useChatState()
  const {updateItem} = useContentPreview()

  return useCallback(
    async (subIssue: DraftOrExistingIssue) => {
      sendEvent('dotcom_chat.activate', {
        target: 'BROWSER_ISSUE_UNLINK_FROM_PARENT',
        mode: 'immersive',
        threadId: getSelectedThread(state)?.id,
        chatReferrer: document.referrer,
        issueRepository: subIssue.repository,
        issueTab: subIssue.tag,
        parentIssueTag: subIssue.parentTag,
      })

      const instructions: TimelineEventTextReference = {
        type: 'text',
        name: `timeline-event: {"type": "unlink-sub-issue", "markdownContent": "Unlinked sub-issue '${subIssue.name}'"}`,
        text: `Unlink the sub issue '${subIssue.tag}' from its parent issue by removing the 'parentTag' field. Return the updated '${subIssue.tag}' draft-issue.`,
      }

      const updatedSubissue = {...subIssue, parentTag: undefined, isUserEdited: false}
      updateItem(updatedSubissue)

      const updatedSubIssueRef =
        updatedSubissue.type === 'existing-issue'
          ? makeReferenceFromExistingIssue(updatedSubissue)
          : makeReferenceFromVersionedItem(updatedSubissue)

      await manager.sendChatMessage({
        thread: getSelectedThread(state),
        content: 'Unlink the sub-issue from its parent issue',
        references: [instructions, updatedSubIssueRef],
        topic: state.currentTopic,
        context: state.context,
        customInstructions: state.customInstructions,
        model: state.model,
      })
    },
    [manager, state, updateItem],
  )
}

// Helper function to convert TreeNode to RelationshipListItem with nested structure
function convertTreeNodesToListItems(nodes: Array<TreeNode<DraftOrExistingIssue>>): RelationshipListItem[] {
  return nodes.map(node => ({
    item: node.item,
    children: convertTreeNodesToListItems(node.children),
  }))
}

export function SubissuesList({issueNode}: {issueNode: TreeNode<DraftOrExistingIssue>}) {
  const onUnlinkSubIssue = useUnlinkSubIssueCallback()

  const title = useMemo(() => {
    if (issueNode.item.type === 'existing-issue') {
      // "New" indicates that we're adding to what already exists
      return 'New sub-issues'
    }
    return 'Sub-issues'
  }, [issueNode])

  const totalChildCount = useMemo(() => {
    if (!issueNode) return 0

    let result = -1 // start at -1 to not count the current node itself
    visit(issueNode, () => result++)
    return result
  }, [issueNode])

  return (
    <RelationshipList
      title={title}
      items={convertTreeNodesToListItems(issueNode.children)}
      totalCount={totalChildCount}
      actions={[
        {
          key: 'unlink',
          label: 'Unlink sub-issue',
          icon: <UnlinkIcon />,
          onSelect: subissueItem => {
            void onUnlinkSubIssue(subissueItem)
          },
        },
      ]}
    />
  )
}
